import type { NodeV2, NodeV2Style } from '@vedalib/editor'
import { useMemo, useRef } from 'react'

import { updateBlock } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { setGridCol, setGridRow } from 'services/Store/Project/mutators/node'
import { getDeviceMode } from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'

import { applyGrid, safeParseGrid } from '../../formControls/Grid/utils'
import type { ISizeValue, SizeUnitType } from '../../formControls/Size/types'
import type { ICoordinates } from '../types'
import {
  DATA_DISPLAY_COL,
  DATA_DISPLAY_ID,
  DATA_DISPLAY_ROW,
  findNodeRef,
  isOffset,
} from '../utils'
import * as s from './Grid.module.scss'

interface IGridResizeControlProps {
  type: 'row' | 'col'
  index: number
  node: NodeV2
  block: Block
  styles: NodeV2Style | null
}

const GridResizeControl: React.FC<IGridResizeControlProps> = ({
  block,
  type,
  index,
  node,
  styles,
}) => {
  const dispatch = useProjectDispatch()
  const device = useProjectContext(getDeviceMode)
  const initialCoord = useRef<ICoordinates>()
  const data = useRef<(ISizeValue | null)[]>()
  const initData = useRef<(ISizeValue | null)[]>()

  const gap = (type === 'row' ? styles?.gridRowsGap : styles?.gridColumnsGap) || 0
  const { items, min } = useMemo(
    () => safeParseGrid(type === 'row' ? styles?.gridRows : styles?.gridColumns),
    [type, styles],
  )

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    initialCoord.current = { x: e.clientX, y: e.clientY }
    let isDragging = false
    let raf: number | null = null
    const nodeRef = findNodeRef(node.id) as HTMLDivElement | null
    const box = nodeRef?.getBoundingClientRect()
    const totalSize = (type == 'col' ? box?.width : box?.height) || 0 - gap * (items.length - 1)

    const convertToPx = (item?: ISizeValue | null, i?: number): number => {
      if (item?.unit === 'px') {
        return item.value || 0
      }
      if (item?.unit === '%') {
        return ((item.value || 0) / 100) * totalSize
      }
      if (item?.unit === 'fr') {
        const frSum = items
          .filter((it) => it?.data?.unit === 'fr')
          .reduce((sum, it) => sum + (it?.data?.value || 0), 0)
        return ((item.value || 0) / frSum) * totalSize
      }
      if (!item) {
        const element = nodeRef?.querySelector(
          `[${DATA_DISPLAY_ID}="${node.id}"][${type === 'col' ? DATA_DISPLAY_COL : DATA_DISPLAY_ROW}="${(i || 0) + 1}"]`,
        )
        const box = element?.getBoundingClientRect()

        return ((type === 'col' ? box?.width : box?.height) || 0) - gap
      }
      return 0
    }

    const convertFromPx = (pxValue: number, unit?: SizeUnitType | null) => {
      if (unit === 'px') {
        return +pxValue.toFixed(0)
      }
      if (unit === '%') {
        return +((pxValue / totalSize) * 100).toFixed(1)
      }
      if (unit === 'fr') {
        const frSum = items
          .filter((it) => it?.data?.unit === 'fr')
          .reduce((sum, it) => sum + (it?.data?.value || 0), 0)
        return +((pxValue / totalSize) * frSum || 0.01).toFixed(2)
      }
      return 0
    }

    initData.current = items.map((item, i) => {
      if (i !== index - 1 && i !== index) {
        return item.data || data.current?.[i] || null
      }

      const prevItem = items[index - 1] || data.current?.[index - 1]
      const nextItem = items[index] || data.current?.[index]

      const newPrevPx = Math.max(convertToPx(prevItem.data, index - 1), 0)
      const newNextPx = Math.max(convertToPx(nextItem.data, index), 0)

      if (i === index - 1) {
        return {
          unit: prevItem?.data?.unit || 'px',
          value: convertFromPx(newPrevPx, prevItem?.data?.unit || 'px'),
        }
      }
      return {
        unit: nextItem?.data?.unit || 'px',
        value: convertFromPx(newNextPx, nextItem?.data?.unit || 'px'),
      }
    })

    const onMouseMove = (event: MouseEvent) => {
      if (raf) {
        return
      }
      raf = requestAnimationFrame(() => {
        raf = null

        if (!isDragging && initialCoord.current && isOffset(initialCoord.current, event)) {
          isDragging = true
        }

        if (/* !disabled && */ isDragging && initialCoord.current && nodeRef) {
          const offsetX = event.clientX - initialCoord.current.x
          const offsetY = event.clientY - initialCoord.current.y

          data.current = items?.map((item, i) => {
            if (i !== index - 1 && i !== index) {
              return item.data
            }

            const delta = type === 'row' ? offsetY : offsetX
            const prevItem = items[index - 1] || initData.current?.[index - 1]
            const nextItem = items[index] || initData.current?.[index]

            const newPrevPx = Math.max(convertToPx(prevItem.data, index - 1) + delta, 0)
            const newNextPx = Math.max(convertToPx(nextItem.data, index) - delta, 0)

            if (i === index - 1) {
              return {
                unit: prevItem?.data?.unit || 'px',
                value: +convertFromPx(newPrevPx, prevItem?.data?.unit || 'px').toFixed(2),
              }
            }
            return {
              unit: nextItem?.data?.unit || 'px',
              value: +convertFromPx(newNextPx, nextItem?.data?.unit || 'px').toFixed(2),
            }
          })

          if (nodeRef && type === 'col') {
            nodeRef.style.gridTemplateColumns = applyGrid({ items: data.current, min })
          }

          if (nodeRef && type === 'row') {
            nodeRef.style.gridTemplateRows = applyGrid({ items: data.current, min })
          }
        }
      })
    }

    const onMouseUp = (event: MouseEvent) => {
      if (raf) {
        cancelAnimationFrame(raf)
      }
      if (node && data.current) {
        event.stopPropagation()
        const mutator = type === 'row' ? setGridRow : setGridCol
        const newBlock = mutator(node?.id, device, {
          items: data.current.map((item, index) => ({ data: item, value: items[index]?.value })),
          min,
        })(block)
        dispatch(updateBlock({ id: block.uuid, value: newBlock }))
      }
      document.removeEventListener('mousemove', onMouseMove, true)
      document.removeEventListener('mouseup', onMouseUp, true)
      data.current = undefined
      nodeRef?.removeAttribute('style')
    }

    document.addEventListener('mousemove', onMouseMove, true)
    document.addEventListener('mouseup', onMouseUp, true)
  }

  return (
    <div
      className={s[`${type}Control`]}
      onMouseDown={handleMouseDown}
      style={
        type === 'row'
          ? {
              transform: `translateY(calc(-50% - ${gap / 2}px))`,
              height: gap,
            }
          : {
              transform: `translateX(calc(-50% - ${gap / 2}px))`,
              width: gap,
            }
      }
    />
  )
}

export default GridResizeControl
